// libraries
import { useEffect, useState } from 'react';
import { Button, Table, Tooltip, Typography } from 'antd';
import { MessageOutlined, TeamOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';

// components
import { SharedTable } from '../../../shared/table';

// services
import * as StatusService from '../../../../services/bb-api/status';

// utils
import { txt } from '../../../../locales/i18n';
import { formatDate } from '../../../../utils/date';
import styled from 'styled-components';

function renderType(type: number): string {
	switch (type) {
		case 1:
			return txt('group.type hidden');

		case 2:
			return txt('group.type password');

		case 3:
			return txt('group.type request');

		case 4:
			return txt('group.type open');

		default:
			return txt('group.type unknown');
	}
}

const membersTitle: JSX.Element = (
	<Tooltip title={txt('global.members')}>
		<TeamOutlined />
	</Tooltip>
);

const messagesTitle: JSX.Element = (
	<Tooltip title={txt('global.messages')}>
		<MessageOutlined />
	</Tooltip>
);

const pastWeekMessagesTitle: JSX.Element = (
	<Tooltip title={txt('group.messages last week')}>
		<MessageOutlined /> (7)
	</Tooltip>
);

export function GroupListContentDesktop(props: any): JSX.Element {
	const [statuses, setStatuses] = useState<Record<string, any>[]>([]);
	const history = useHistory();
	const location = useLocation();

	useEffect(() => {
		async function getResources(): Promise<void> {
			const statuses: Record<string, any>[] = await StatusService.query();
			setStatuses(statuses);
		}

		getResources();
	}, []);

	function renderUser(username: string, group: Record<string, any>): JSX.Element {
		function handleNavigate(event: React.MouseEvent<HTMLElement, MouseEvent>): void {
			event.stopPropagation();
			history.push(`/users/${group.userId}`);
		}

		return (
			<Tooltip title={`${txt('global.open')} ${username}`}>
				<Button type="link" onClick={handleNavigate} className="user-cell-container">
					<Typography.Link>{username}</Typography.Link>
					<Typography.Text type="secondary">{group.userMunicipality}</Typography.Text>
				</Button>
			</Tooltip>
		);
	}

	function renderStatus(statusId: number): JSX.Element | null {
		const status: Record<string, any> | undefined = statuses.find((status: Record<string, any>) => {
			return status?.id === statusId;
		});

		if (status === undefined) return null;
		return status.name;
	}

	return (
		<StyledTable
			rowKey="id"
			loading={props.loading}
			dataSource={props.dataSource}
			onRow={(group: Record<string, any>) => ({
				onClick: () => {
					history.replace({ hash: String(group.id), search: location.search })
					history.push(`/groups/${group.id}`)
				},
			})}
			rowClassName={(group: Record<string, any>) => (group.type === 2 ? 'group-password' : '')}
		>
			<Table.Column dataIndex="created" title={txt('global.created')} width="24ch" render={formatDate} />
			<Table.Column dataIndex="id" title="ID" width="8ch" align="center" />
			<Table.Column dataIndex="name" title={txt('global.name')} width="24ch" ellipsis />
			<Table.Column dataIndex="municipality" title={txt('global.municipality')} width="18ch" />
			<Table.Column dataIndex="type" title="Type" width="14ch" render={renderType} />
			<Table.Column dataIndex="statusId" title="Status" width="24ch" render={renderStatus} />
			<Table.Column dataIndex="members" title={membersTitle} width="12ch" align="center" />
			<Table.Column dataIndex="messages" title={messagesTitle} width="12ch" align="center" />
			<Table.Column dataIndex="pastWeekMessages" title={pastWeekMessagesTitle} width="12ch" align="center" />
			<Table.Column dataIndex="username" title={txt('global.user')} width="38ch" render={renderUser} />
		</StyledTable>
	);
}

const StyledTable = styled(SharedTable)`
	.user-cell-container {
		display: flex;
		gap: 1ch;
	}
`;
